import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Facebook, Twitter, Linkedin, Send, User, Calendar, Clock } from "lucide-react";
import Sidebar from "../components/Sidebar";
import ArticleCard from "../components/ArticleCard";
import BannerAd from "../components/BannerAd";
import { fetchArticle, fileUrl } from "../api";

const ArticlePage = () => {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setLoading(true);
    setNotFound(false);
    fetchArticle(slug)
      .then((data) => {
        setArticle(data.article);
        setRelated(data.related || []);
      })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return <div className="max-w-3xl mx-auto px-4 py-20 text-center text-gray-400">Caricamento...</div>;
  }

  if (notFound || !article) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-20 text-center">
        <h1 className="font-serif-display text-3xl text-sq-navy">Articolo non trovato</h1>
        <Link to="/" className="text-sq-orange mt-4 inline-block">Torna alla home</Link>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-6 py-8 fade-in">
      <div className="flex items-center gap-2 text-sm text-gray-500 mb-6">
        <Link to="/" className="hover:text-sq-orange">Home</Link>
        <span>/</span>
        <Link to={`/categoria/${article.categorySlug}`} className="hover:text-sq-orange">
          {article.category}
        </Link>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
        <article className="lg:col-span-2">
          <span className="bg-sq-orange text-white text-[11px] uppercase tracking-wide font-semibold px-3 py-1">
            {article.category}
          </span>
          <h1 className="font-serif-display text-3xl md:text-4xl leading-tight text-sq-navy mt-4">
            {article.title}
          </h1>
          <div className="flex flex-wrap items-center gap-5 text-sm text-gray-500 mt-4 pb-5 border-b border-sq">
            <span className="flex items-center gap-1"><User size={14} /> {article.author}</span>
            <span className="flex items-center gap-1"><Calendar size={14} /> {article.date}</span>
            <span className="flex items-center gap-1"><Clock size={14} /> {article.readTime} di lettura</span>
          </div>

          {article.videoUrl ? (
            <video
              src={fileUrl(article.videoUrl)}
              poster={fileUrl(article.image)}
              controls
              playsInline
              className="w-full max-h-[480px] bg-black rounded-sm mt-6"
            />
          ) : (
            <img
              src={fileUrl(article.image)}
              alt={article.title}
              className="w-full h-[380px] object-cover rounded-sm mt-6"
            />
          )}

          <div className="prose max-w-none mt-6">
            <p className="text-lg leading-relaxed text-gray-700 font-medium">{article.excerpt}</p>
            {(article.content || []).map((p, i) => (
              <p key={i} className="text-[17px] leading-[1.8] text-gray-800 mt-5">{p}</p>
            ))}
            {article.rss_imported && article.sourceName && (
              <p className="text-xs text-gray-400 mt-6 italic">
                Articolo originale rielaborato dalla redazione. Spunto da: {article.sourceName}.
              </p>
            )}
          </div>

          <div className="flex items-center gap-3 mt-8 pt-6 border-t border-sq">
            <span className="text-sm font-medium text-sq-navy">Condividi:</span>
            {[Facebook, Twitter, Linkedin, Send].map((Icon, i) => (
              <a
                key={i}
                href="#"
                className="w-9 h-9 rounded-full bg-sq-light flex items-center justify-center text-sq-navy hover:bg-sq-orange hover:text-white transition-colors"
              >
                <Icon size={15} />
              </a>
            ))}
          </div>

          <div className="my-10 flex justify-center">
            <BannerAd id="article-inline" label="Spazio Pubblicitario" />
          </div>

          {related.length > 0 && (
            <div>
              <span className="sq-heading">Articoli correlati</span>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 mt-5">
                {related.map((a) => (
                  <ArticleCard key={a.id} article={a} />
                ))}
              </div>
            </div>
          )}
        </article>
        <Sidebar />
      </div>
    </div>
  );
};

export default ArticlePage;
